import { FormControl, FormHelperText, Stack } from '@mui/material';
import { DateRange, TimePickerProps } from '@mui/x-date-pickers-pro';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import IncFormHelperText, {
  IncFormHelperTextProps,
} from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useController } from 'react-hook-form';

export type TimeRangePickerFieldProps = {
  name: string;
  helperText?: string;
  helperTextSeverity?: IncFormHelperTextProps['severity'];
  fromLabel: string;
  toLabel: string;
  disabled?: boolean;
  'data-testid'?: string;
} & Partial<TimePickerProps>;

const TimeRangePickerField = ({
  name,
  helperText,
  helperTextSeverity,
  fromLabel,
  toLabel,
  minutesStep,
  disabled,
  'data-testid': dataTestId = 'TimeRangePickerField',
}: TimeRangePickerFieldProps) => {
  const {
    field: { ref, onChange: rhfOnChange, value, ...fieldRest },
    fieldState: { error },
  } = useController({ name });

  const fieldValue: DateRange<DateTime> = useMemo(() => {
    if (value) {
      // eslint-disable-next-line array-callback-return
      return value.map((date: string | DateTime) => {
        if (typeof date === 'string') {
          return DateTime.fromISO(date);
          // @ts-ignore
        } else if (date?.ts && !date?.zone) {
          // @ts-ignore
          return DateTime.fromMillis(date.ts);
        }
        return date;
      });
    } else return value;
  }, [value]);

  const handleFrom = newValue => {
    const newFromTime = newValue;
    const newToTime = fieldValue?.[1];

    rhfOnChange([newFromTime, newToTime]);
  };

  const handleTo = newValue => {
    const newToTime = newValue;
    const newFromTime = fieldValue?.[0];

    rhfOnChange([newFromTime, newToTime]);
  };

  return (
    <FormControl error={!!error} {...fieldRest} data-testid={dataTestId}>
      <Stack display="flex" direction="row" spacing={1}>
        <TimePicker
          label={fromLabel}
          value={fieldValue?.[0]}
          onChange={newValue => handleFrom(newValue)}
          skipDisabled
          slotProps={{
            textField: {
              error: !!error,
              //@ts-ignore
              'data-testid': `${dataTestId}.From.TimePicker`,
              size: 'small',
            },
          }}
          minutesStep={minutesStep}
          disabled={disabled}
        />
        <TimePicker
          label={toLabel}
          value={fieldValue?.[1]}
          onChange={newValue => handleTo(newValue)}
          skipDisabled
          slotProps={{
            textField: {
              error: !!error,
              //@ts-ignore
              'data-testid': `${dataTestId}.To.TimePicker`,
              size: 'small',
            },
          }}
          minutesStep={minutesStep}
          disabled={disabled}
        />
      </Stack>
      <FormHelperText>
        <IncFormHelperText
          error={error}
          helperText={helperText}
          severity={helperTextSeverity}
          data-testid={`${dataTestId}.HelperText`}
        />
      </FormHelperText>
    </FormControl>
  );
};

export default TimeRangePickerField;
